import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient, ApiError } from '../client';

export interface UseSessionWatcherOptions {
  /** Fires when the session is determined to be expired (idle timeout or 401 from poll). */
  onExpired: () => void;
  /** Idle timeout (ms) before the user is logged out. Default: 30 minutes. */
  idleTimeoutMs?: number;
  /** Time (ms) before idle timeout to show the warning dialog. Default: 2 minutes. */
  warningThresholdMs?: number;
  /** Interval (ms) for proactive `/auth/me/` polls. Default: 5 minutes. */
  pollIntervalMs?: number;
  /** Whether the watcher is active. Default: true. */
  enabled?: boolean;
}

export interface SessionWatcherState {
  /** True when the idle warning should be shown. */
  showWarning: boolean;
  /** Milliseconds remaining until automatic logout. */
  msUntilLogout: number;
  /** Resets the idle timer and re-validates the session. */
  extendSession: () => Promise<void>;
}

const DEFAULT_IDLE_TIMEOUT_MS = 30 * 60 * 1000;
const DEFAULT_WARNING_THRESHOLD_MS = 2 * 60 * 1000;
const DEFAULT_POLL_INTERVAL_MS = 5 * 60 * 1000;
const TICK_INTERVAL_MS = 15 * 1000;
const ACTIVITY_EVENTS = [
  'mousedown',
  'mousemove',
  'keydown',
  'scroll',
  'touchstart',
  'click',
] as const;

/**
 * Proactive session watcher: closes the gap between cookie expiry and the next
 * API call. Without this, a user can sit on a stale page indefinitely because
 * the only logout path is reactive (fires on a 401 from a future request).
 *
 * The hook does two things in parallel:
 *  1. Tracks user activity and fires `onExpired` after `idleTimeoutMs` of
 *     inactivity (with a warning at `idleTimeoutMs - warningThresholdMs`).
 *  2. Polls `/auth/me/` every `pollIntervalMs` and on tab refocus; a 401
 *     response also fires `onExpired`.
 */
export function useSessionWatcher({
  onExpired,
  idleTimeoutMs = DEFAULT_IDLE_TIMEOUT_MS,
  warningThresholdMs = DEFAULT_WARNING_THRESHOLD_MS,
  pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
  enabled = true,
}: UseSessionWatcherOptions): SessionWatcherState {
  const queryClient = useQueryClient();
  const lastActivityRef = useRef<number>(Date.now());
  const expiredFiredRef = useRef(false);
  const onExpiredRef = useRef(onExpired);
  const [showWarning, setShowWarning] = useState(false);
  const [msUntilLogout, setMsUntilLogout] = useState(idleTimeoutMs);

  useEffect(() => {
    onExpiredRef.current = onExpired;
  }, [onExpired]);

  const triggerExpired = useCallback(() => {
    if (expiredFiredRef.current) return;
    expiredFiredRef.current = true;
    setShowWarning(false);
    queryClient.clear();
    // Best-effort server-side logout so cookies are cleared. Ignored on failure
    // because the client-side redirect must happen regardless.
    apiClient.post('/auth/logout/').catch(() => undefined);
    onExpiredRef.current();
  }, [queryClient]);

  const verifySession = useCallback(async () => {
    try {
      await apiClient.get('/auth/me/');
      return true;
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        triggerExpired();
      }
      return false;
    }
  }, [triggerExpired]);

  const extendSession = useCallback(async () => {
    lastActivityRef.current = Date.now();
    setShowWarning(false);
    setMsUntilLogout(idleTimeoutMs);
    await verifySession();
  }, [idleTimeoutMs, verifySession]);

  // Activity listeners — reset idle timer on user interaction.
  useEffect(() => {
    if (!enabled) return;
    const handleActivity = () => {
      lastActivityRef.current = Date.now();
    };
    ACTIVITY_EVENTS.forEach((evt) => {
      window.addEventListener(evt, handleActivity, { passive: true });
    });
    return () => {
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, handleActivity));
    };
  }, [enabled]);

  // Idle tick — every TICK_INTERVAL_MS, compute remaining time and fire
  // warning / expiry as thresholds are crossed.
  useEffect(() => {
    if (!enabled) return;
    const tick = () => {
      const idleMs = Date.now() - lastActivityRef.current;
      const remaining = idleTimeoutMs - idleMs;
      setMsUntilLogout(Math.max(0, remaining));
      if (idleMs >= idleTimeoutMs) {
        triggerExpired();
        return;
      }
      const shouldWarn = idleMs >= idleTimeoutMs - warningThresholdMs;
      setShowWarning(shouldWarn);
    };
    tick();
    const id = window.setInterval(tick, TICK_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [enabled, idleTimeoutMs, warningThresholdMs, triggerExpired]);

  // Proactive session poll — verifies the cookie session is still valid on the
  // server, both on a fixed interval and immediately when the tab regains focus.
  useEffect(() => {
    if (!enabled) return;
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        verifySession();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    const id = window.setInterval(verifySession, pollIntervalMs);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.clearInterval(id);
    };
  }, [enabled, pollIntervalMs, verifySession]);

  return { showWarning, msUntilLogout, extendSession };
}
